import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";

import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import {
  type GetPlacementApplicationsResponse,
  type PlacementApplicationData,
  type PlacementApplicationInterviewInclude,
  type PlacementApplicationPlacementCandidateBanInclude,
  type PlacementApplicationPlacementInclude,
  type PlacementApplicationWorkplaceInclude,
} from "../types/fetchPlacementApplications.schema";
import {
  type PlacementIncludedApplication,
  type PlacementIncludedInterview,
} from "../types/fetchPlacements.schema";

function findPlacementInclude(
  placementId: string,
  included: GetPlacementApplicationsResponse["included"]
): PlacementApplicationPlacementInclude {
  const placement = (included ?? []).find(
    (item): item is PlacementApplicationPlacementInclude =>
      item.type === API_TYPES.placement && item.id === placementId
  );

  if (!placement) {
    throw new Error(`Placement include not found for id: ${placementId}`);
  }

  return placement;
}

function findWorkplaceInclude(
  workplaceId: string,
  included: GetPlacementApplicationsResponse["included"]
): PlacementApplicationWorkplaceInclude {
  const workplace = (included ?? []).find(
    (item): item is PlacementApplicationWorkplaceInclude =>
      item.type === API_TYPES.workplace && item.id === workplaceId
  );

  if (!workplace) {
    throw new Error(`Workplace include not found for id: ${workplaceId}`);
  }

  return workplace;
}

function findInterviewIncludes(
  applicationId: string,
  included: GetPlacementApplicationsResponse["included"]
): PlacementApplicationInterviewInclude[] {
  return (included ?? []).filter(
    (item): item is PlacementApplicationInterviewInclude =>
      item.type === API_TYPES.interview &&
      item.relationships?.placementApplication?.data.id === applicationId
  );
}

function buildApplication(applicationData: PlacementApplicationData): PlacementIncludedApplication {
  return {
    type: API_TYPES.placementApplication,
    id: applicationData.id,
    attributes: {
      appliedDate: applicationData.attributes.appliedDate,
      status: applicationData.attributes.status,
      updatedAt: applicationData.attributes.updatedAt,
    },
  };
}

function findBanIsExcused(
  banId: string | undefined,
  included: GetPlacementApplicationsResponse["included"]
): boolean | undefined {
  if (!isDefined(banId)) {
    return undefined;
  }

  const ban = (included ?? []).find(
    (item): item is PlacementApplicationPlacementCandidateBanInclude =>
      item.type === API_TYPES.placementCandidateBan && item.id === banId
  );

  return ban?.meta.isExcused;
}

function buildInterviews(
  interviewIncludes: PlacementApplicationInterviewInclude[],
  included: GetPlacementApplicationsResponse["included"]
): PlacementIncludedInterview[] {
  const interviews: PlacementIncludedInterview[] = [];

  for (const interview of interviewIncludes) {
    const interviewType = interview.attributes.type;
    if (!isDefined(interviewType)) {
      continue;
    }

    const isBanExcused = findBanIsExcused(
      interview.relationships?.placementCandidateBan?.data.id,
      included
    );

    interviews.push({
      type: API_TYPES.interview,
      id: interview.id,
      attributes: {
        start: interview.attributes.start,
        end: interview.attributes.end,
        createdAt: interview.attributes.createdAt,
        status: interview.attributes.status,
        type: interviewType,
        scheduleStatus: interview.attributes.scheduleStatus,
        verificationStatus: interview.attributes.verificationStatus,
        workerManualEta: interview.attributes.workerManualEta,
        attendanceStatus: interview.attributes.attendanceStatus,
      },
      meta: {
        ...interview.meta,
        ...(isDefined(isBanExcused) && { isBanExcused }),
      },
      relationships: {
        placementApplication: {
          data: {
            type: API_TYPES.placementApplication,
            id: interview.relationships?.placementApplication?.data.id ?? "",
          },
        },
      },
    });
  }

  return interviews;
}

/**
 * Transforms a placement-application response item (application-centric)
 * into the existing PlacementWithDetails shape (placement-centric).
 *
 * This allows downstream components (AppliedPlacementCard, SentApplicationsAccordionSection)
 * to work unchanged during the migration.
 */
export function transformPlacementApplicationToPlacementWithDetails(
  applicationData: PlacementApplicationData,
  included: GetPlacementApplicationsResponse["included"]
): PlacementWithDetails {
  const placementInclude = findPlacementInclude(
    applicationData.relationships.placement.data.id,
    included
  );

  const workplaceInclude = findWorkplaceInclude(
    applicationData.relationships.workplace.data.id,
    included
  );

  const interviewIncludes = findInterviewIncludes(applicationData.id, included);

  const coordinates = placementInclude.attributes.coordinates
    ? [
        placementInclude.attributes.coordinates.longitude,
        placementInclude.attributes.coordinates.latitude,
      ]
    : [0, 0];

  return {
    // Placement attributes
    id: placementInclude.id,
    status: placementInclude.attributes.status,
    title: placementInclude.attributes.title,
    jobShifts: [],
    workerTypes: placementInclude.attributes.workerTypes,
    payRange: placementInclude.attributes.payRange,
    address: placementInclude.attributes.address ?? { formatted: undefined },
    coordinates,
    interviewInstructions: placementInclude.attributes.interviewInstructions,
    distance: placementInclude.meta?.distance,

    // Placement attributes not available from this endpoint
    perks: undefined,
    validUntil: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    confirmedAt: undefined,
    originalJobLink: undefined,
    webApplicationLink: undefined,
    signOnBonus: undefined,
    schedule: undefined,
    transparencyPayRates: undefined,
    tags: undefined,

    // Meta from application
    isFavorited: applicationData.meta.isFavorited,
    isLicenseEligible: applicationData.meta.isLicenseEligible,
    isDocumentRequirementValid: applicationData.meta.isDocumentRequirementValid,
    isExemptFromDocumentRequirements: applicationData.meta.isExemptFromDocumentRequirements,
    allowRequestInterview: applicationData.meta.allowRequestInterview,
    isRecruiterScreeningEnabled: applicationData.meta.isRecruiterScreeningEnabled,
    applicationMethod: applicationData.meta.applicationMethod,

    // Meta not available from this endpoint
    scoreBreakdown: undefined,
    score: undefined,
    baseRate: undefined,
    totalRate: undefined,
    totalRateBreakdown: undefined,
    matchedType: undefined,
    isEasyScheduling: undefined,

    // Workplace
    workplace: {
      id: workplaceInclude.id,
      name: workplaceInclude.attributes.name,
      state: workplaceInclude.attributes.state ?? "",
      type: workplaceInclude.attributes.type,
      photos: workplaceInclude.attributes.photos,
      tmz: workplaceInclude.attributes.tmz ?? "",
    },

    // Application (constructed from primary data)
    applications: [buildApplication(applicationData)],

    // Interviews
    interviews: buildInterviews(interviewIncludes, included),

    // Not available from this endpoint
    interviewSettings: undefined,
    workerRequirements: undefined,
    credentialRequirements: undefined,
    candidateBans: undefined,
    hiringManagers: undefined,
    workerFavoritePlacementId: undefined,
  };
}
